import "./base.js";
import "../../../shared/events.js";
import "./mixins/recoverable_error.js";
// NOTE: The step subclasses (./steps/*.js) were previously eagerly globbed
// here, but that creates a circular dependency under Vite's ESM: step files
// import this file back, and this file hasn't finished evaluating yet, so
// AppointmentBooking.Wizard.Views.Step is still undefined when the subclass
// `extends` clause evaluates. The glob is moved to views/wizard.js which
// imports step.js first, then the step subclasses.

// TODO: Show "you need to change your selection" messages specific to the attribute that was just changed
const fieldSelector = '[data-appointment-booking-wizard-behaviour="field-input"]';

AppointmentBooking.Wizard.Views.Step = class Step extends (
  AppointmentBooking.Wizard.Views.Mixins.RecoverableError(AppointmentBooking.Wizard.Views.Base)
) {
  static {
    this.prototype.tagName = "div";
    this.prototype.className = "appointment-booking-wizard-step";
    this.prototype.name = "Untitled Wizard Step";

    this.prototype.events = {
      [`input ${fieldSelector}`]: "handleFieldInput",
      [`focusout .dirty ${fieldSelector}`]: "handleFieldChange", // Use focusout instead of change to ensure validation is performed if the user types to make the field valid then deletes that change without blurring the field
      [`click ${fieldSelector}:not(:input)`]: "handleFieldChange", // function that returns true if the step can be skipped
    };
    this.prototype.beforeShow = undefined; // A function to execute before a page is shown
    this.prototype.afterShow = undefined;
  }

  constructor() {
    super(...arguments);
    this.validatePrerequisites = this.validatePrerequisites.bind(this);
    this.validateRequirements = this.validateRequirements.bind(this);
    this.performReset = this.performReset.bind(this);
    this.initialize = this.initialize.bind(this);
    this.handleBookingChanged = this.handleBookingChanged.bind(this);
    this.render = this.render.bind(this);
    this.updateFieldsDirtyState = this.updateFieldsDirtyState.bind(this);
    this.showValidity = this.showValidity.bind(this);
    this.markFieldDirty = this.markFieldDirty.bind(this);
    this.scrollToInvalidInput = this.scrollToInvalidInput.bind(this);
    this.getPageTitle = this.getPageTitle.bind(this);
    this.listenToIfEnabled = this.listenToIfEnabled.bind(this);
    this.listenToIfRenderable = this.listenToIfRenderable.bind(this);
    this.renderField = this.renderField.bind(this);
    this.areFieldsValid = this.areFieldsValid.bind(this);
    this.getInvalidFields = this.getInvalidFields.bind(this);
    this.setAttributeInput = this.setAttributeInput.bind(this);
    this.setInputValue = this.setInputValue.bind(this);
    this.setCheckboxValue = this.setCheckboxValue.bind(this);
    this.setRadioButtonValue = this.setRadioButtonValue.bind(this);
    this.storeInputAttribute = this.storeInputAttribute.bind(this);
    this.isAttributeInputValid = this.isAttributeInputValid.bind(this);
    this.isInputValid = this.isInputValid.bind(this);
    this.isTelephoneInputValid = this.isTelephoneInputValid.bind(this);
    this.isDateOfBirthInputValid = this.isDateOfBirthInputValid.bind(this);
    this.isInputMarkedInvalid = this.isInputMarkedInvalid.bind(this);
    this.getInputContainer = this.getInputContainer.bind(this);
    this.matchesInputValue = this.matchesInputValue.bind(this);
    this.getInputForAttribute = this.getInputForAttribute.bind(this);
    this.getInputValue = this.getInputValue.bind(this);
    this.getCheckboxValue = this.getCheckboxValue.bind(this);
    this.getRadioButtonValue = this.getRadioButtonValue.bind(this);
    this.getTextInputValue = this.getTextInputValue.bind(this);
    this.eachInputField = this.eachInputField.bind(this);
  }

  static lookup(stepName) {
    const stepClass = AppointmentBooking.Wizard.Views[`${stepName}Step`];
    // TODO: Assert existence of step
    // assert.presence(stepClass, "Unknown Wizard Step: #{stepName}")
    return stepClass;
  }

  // a function that returns a promise which resolves if everything is ready to start this step
  validatePrerequisites() {
    const deferred = new jQuery.Deferred();
    if (this.arePrerequisitesValid()) {
      return deferred.resolve();
    } else {
      return deferred.reject();
    }
  }

  // a function that returns a promise which resolves if everything is ready to complete this step and continue
  validateRequirements() {
    const deferred = new jQuery.Deferred();
    if (this.areRequirementsValid()) {
      return deferred.resolve();
    } else {
      return deferred.reject();
    }
  }

  // a function that returns a promise which resolves once the state has been reset and it's safe to back out of the current step
  performReset() {
    this.reset();
    return new jQuery.Deferred().resolve();
  }

  arePrerequisitesValid() {
    return true;
  } // a function that returns a true if everything is ready to start this step
  areRequirementsValid() {
    return true;
  } // a function that returns a true if everything is ready to complete this step and continue
  reset() {} // A function that rolls back unwanted changes from the current step such that we could transition to an earlier step without issue
  autoContinue() {
    return false;
  } // Does this page automatically continue to the next page when complete, and therefore does not need navigation controls?
  isDisabled() {
    return false;
  } // function that returns true if the step should not be shown
  isSkippable() {
    return false;
  } // A function to execute after a page is attached to the DOM

  initialize() {
    super.initialize(...arguments);
    return this.listenTo(
      this.model,
      "change relational:change:visit_type_rule_selections relational:change:verify_id_answers",
      this.handleBookingChanged,
    );
  }

  // EVENT HANDLERS

  handleFieldInput(e) {
    const input = jQuery(e.currentTarget);
    this.markFieldDirty(input, { validate: this.isInputMarkedInvalid(input) });
    return this.storeInputAttribute(input, this.model);
  }

  handleFieldChange(e) {
    const input = jQuery(e.currentTarget);
    this.markFieldDirty(input);
    return this.storeInputAttribute(input, this.model);
  }

  handleBookingChanged() {
    return jQuery.each(this.model.changedAttributes(), this.setAttributeInput);
  }

  // RENDERING

  render() {
    if (!this.renderable) {
      return this;
    }

    super.render(...arguments);
    this.updateFieldsDirtyState();

    return this;
  }

  updateFieldsDirtyState() {
    const defaults = this.model.defaults();
    return this.eachInputField((input) => {
      const attributeName = jQuery(input).attr("name");
      if (!this.matchesInputValue(input, defaults[attributeName])) {
        return this.markFieldDirty(input);
      }
    });
  }

  showValidity(...fieldNames) {
    if (fieldNames.length) {
      return this.eachInputField((input) => {
        const attributeName = jQuery(input).attr("name");
        if (fieldNames.indexOf(attributeName) > -1) {
          return this.markFieldDirty(input);
        }
      });
    } else {
      return this.eachInputField(this.markFieldDirty);
    }
  }

  markFieldDirty(input, options = {}) {
    options = _.defaults({}, options, { validate: true });

    const container = this.getInputContainer(input);
    container.addClass("dirty");
    if (options.validate) {
      const fieldInputs = container.find(":input").toArray();
      const valid = _.every(fieldInputs, this.isInputValid);
      container.toggleClass("invalid", !valid);
      return container.toggleClass("valid", valid);
    }
  }

  scrollToInvalidInput() {
    const firstInvalidInput = this.$(`${fieldSelector}:invalid`).filter(":visible").first().closest(".field");
    return typeof firstInvalidInput[0]?.scrollIntoView === "function"
      ? firstInvalidInput[0].scrollIntoView({ behavior: "smooth" })
      : undefined;
  }

  // API

  getPageTitle() {
    return this.name;
  }

  // PRIVATE

  listenToIfEnabled(model, event, callback) {
    return this.listenTo(model, event, (...args) => {
      if (!this.isDisabled()) {
        return callback(...Array.from(args || []));
      }
    });
  }

  listenToIfRenderable(model, event, callback) {
    return this.listenTo(model, event, (...args) => {
      if (this.renderable) {
        return callback(...Array.from(args || []));
      }
    });
  }

  // Convenience method for replacing fields with their subviews
  renderField(placeholderSelector, fieldView) {
    const target = this.$(placeholderSelector);
    fieldView.render().$el.addClass(target.attr("class")); // retain classes on the target
    return this.$(target).replaceWith(fieldView.render().el);
  }

  // Returns true if all the field inputs are valid
  areFieldsValid() {
    this.ensureRendered();
    return !this.getInvalidFields().length;
  }

  getInvalidFields() {
    const fields = [];
    this.eachInputField((input) => {
      if (!this.isInputValid(input)) {
        return fields.push(input);
      }
    });
    return fields;
  }

  // Used to store input values as attributes on a model
  setAttributeInput(inputName, value) {
    const input = this.$(`[name='${inputName}']`);
    if (input.is(":input")) {
      return this.setInputValue(input, value);
    } // The element might not be an editable input, e.g. a button whose value we write to the model when clicked
  }

  setInputValue(input, value) {
    input = jQuery(input);

    if (this.matchesInputValue(input, value)) {
      return;
    }

    // console.debug("setting '#{input.attr('name')}' input to", value)

    if (input.is(":checkbox")) {
      return this.setCheckboxValue(input, value);
    } else if (input.is(":radio")) {
      return this.setRadioButtonValue(input, value);
    } else {
      return input.val(value);
    }
  }

  setCheckboxValue(input, value) {
    return input.attr("checked", value);
  }

  setRadioButtonValue(input, value) {
    return input.each((i, element) => jQuery(element).attr("checked", jQuery(element).val() === `${value}`));
  }

  // Used to store input values as attributes on a model
  storeInputAttribute(input, model) {
    let match;
    input = jQuery(input);
    let name = input.attr("name");
    const value = this.getInputValue(input);

    // Store an attribute on a nested model (e.g. visit_type_rule_selections[1][option_id])
    if ((match = /(.*)\[(.*)\]\[(.*)\]/.exec(name))) {
      name = match[1];
      const id = match[2];
      const key = match[3];

      return model.set(name, { id, [key]: value }, { remove: false });
    } else {
      if (model.get(name) === value) {
        return;
      }
      // console.debug("storing", value, 'as', name, 'on', model)
      return model.set(name, value);
    }
  }

  isAttributeInputValid(attributeName) {
    return this.isInputValid(this.getInputForAttribute(attributeName));
  }

  // Check if the field is valid. Returns true if the element does not support validity, e.g. a button
  isInputValid(element) {
    if (!jQuery(element).prop("validity")) {
      return true;
    }

    if (jQuery(element).is('[type="tel"]')) {
      return this.isTelephoneInputValid(element);
    } else if (jQuery(element).is('[data-type="dob"]')) {
      return this.isDateOfBirthInputValid(element);
    } else {
      return jQuery(element)[0].checkValidity();
    }
  }

  isTelephoneInputValid(element) {
    const html5Valid = jQuery(element)[0].checkValidity();
    if (!html5Valid) {
      return false;
    }

    const value = jQuery(element).val();
    if (value.match(/^\s*$/g)) {
      return true;
    }

    // Use libphonenumber-js for validation (imported in wizard.js).
    // Handles area codes, exchange rules, and international formats automatically.
    const phoneNumber = window.libphonenumber.parsePhoneNumberFromString(value, "US");
    return phoneNumber != null && phoneNumber.isValid();
  }

  isDateOfBirthInputValid(element) {
    jQuery(element).next(".validation-success").empty();

    const html5Valid = jQuery(element)[0].checkValidity();
    if (!html5Valid) {
      return false;
    }

    const value = jQuery(element).val();

    // Skip additional validation if the input is blank
    if (value.match(/^\s*$/g)) {
      return true;
    }

    const date = AppointmentBooking.Wizard.Models.Booking.dateOfBirthMoment(value);

    // Invalid if moment can't understand the input
    if (!date.isValid()) {
      return false;
    }

    // Invalid if greater than the current date
    if (date.isAfter(new Date())) {
      return false;
    }

    // Invalid if less than 1900
    if (date.isBefore("1900-01-01")) {
      return false;
    }

    jQuery(element)
      .next(".validation-success")
      .html(`${date.format("MMMM Do, YYYY")} (${moment().diff(date, "years")} years old)`);

    return true;
  }

  isInputMarkedInvalid(input) {
    return this.getInputContainer(input).hasClass("invalid");
  }

  getInputContainer(input) {
    let container = jQuery(input).closest(".field");
    if (!container.length) {
      container = jQuery(input);
    }
    return container;
  }

  matchesInputValue(input, value) {
    let inputValue = this.getInputValue(input);
    if (inputValue == null) {
      inputValue = "";
    }
    if (value == null) {
      value = "";
    }

    return inputValue === value;
  }

  getInputForAttribute(attributeName) {
    const input = this.$(fieldSelector).filter(`[name='${attributeName}']:input`);
    return this.assert.element(input, `Could not find input for attribute ${attributeName}`);
  }

  getInputValue(input) {
    input = jQuery(input);

    if (input.is(":checkbox")) {
      return this.getCheckboxValue(input);
    } else if (input.is(":radio")) {
      return this.getRadioButtonValue(input);
    } else if (input.is(":input")) {
      return this.getTextInputValue(input);
    } else {
      return input.attr("value");
    }
  }

  getCheckboxValue(input) {
    if (input.is(":checked")) {
      return true;
    } else {
      return false;
    }
  }

  getRadioButtonValue(input) {
    let value;
    if (input.is(":checked")) {
      value = input.val();
    }
    // Coerce boolean strings to booleans
    if (value === "true") {
      return true;
    }
    if (value === "false") {
      return false;
    }
    return value;
  }

  getTextInputValue(input) {
    const value = input.val();

    if (input.data("field-input-type") === "integer" && value) {
      return parseInt(value);
    }
    return value;
  }

  eachInputField(fn) {
    return this.$(fieldSelector)
      .filter(":input")
      .each((i, input) => fn(input));
  }
};
